import { useState, useCallback } from "react";
import svgPaths from "../imports/svg-0atpewdd5x";

interface Destination {
	id: string;
	number: number;
	name: string;
	startDate: string;
	endDate: string;
	nights: number;
	hasAccommodation: boolean;
}

interface DraggableRowProps {
	destination: Destination;
	index: number;
	moveDestination: (dragIndex: number, hoverIndex: number) => void;
	updateNights: (id: string, delta: number) => void;
	toggleAccommodation: (id: string) => void;
}

function DestinationRow({ destination, updateNights, toggleAccommodation }: Omit<DraggableRowProps, "index" | "moveDestination">) {
	return (
		<div className="h-[78.613px] relative shrink-0 w-full group">
			<div aria-hidden="true" className="absolute border-[#dde9ed] border-b-[0.625px] border-solid inset-0 pointer-events-none" />
			<div className="flex flex-row items-center size-full">
				<div className="content-stretch flex gap-[31.992px] items-center pb-[0.625px] px-[23.994px] relative size-full">
					{/* Destination Info */}
					<div className="flex-[1_0_0] h-[37.988px] min-h-px min-w-px relative">
						<div className="bg-clip-padding border-0 border-[transparent] border-solid content-stretch flex gap-[11.992px] items-center relative size-full">
							{/* Number Badge */}
							<div className="relative rounded-[20971500px] shrink-0 size-[15.996px]">
								<div aria-hidden="true" className="absolute border-[#615fff] border-[1.875px] border-solid inset-0 pointer-events-none rounded-[20971500px]" />
								<div className="bg-clip-padding border-0 border-[transparent] border-solid content-stretch flex items-center justify-center px-[5.459px] py-[1.875px] relative size-full">
									<div className="h-[15px] relative shrink-0" style={{ width: destination.number >= 10 ? "10px" : "5.078px" }}>
										<div className="bg-clip-padding border-0 border-[transparent] border-solid relative size-full">
											<p className="absolute font-['Inter:Semi_Bold',sans-serif] font-semibold leading-[15px] left-0 not-italic text-[#615fff] text-[10px] top-[0.88px] tracking-[0.1172px] whitespace-nowrap">
												{destination.number}
											</p>
										</div>
									</div>
								</div>
							</div>

							{/* Name and Dates */}
							<div className="h-[37.988px] relative shrink-0 flex-1">
								<div className="bg-clip-padding border-0 border-[transparent] border-solid content-stretch flex flex-col gap-[1.992px] items-start relative size-full">
									<div className="h-[20px] relative shrink-0 w-full">
										<p className="absolute font-['Inter:Semi_Bold',sans-serif] font-semibold leading-[20px] left-0 not-italic text-[#0f3e4a] text-[14px] top-[0.25px] tracking-[-0.1504px] whitespace-nowrap">
											{destination.name}
										</p>
									</div>
									<div className="h-[15.996px] relative shrink-0 w-full">
										<p className="absolute font-['Inter:Regular',sans-serif] font-normal leading-[16px] left-0 not-italic text-[#658c96] text-[12px] top-[0.63px] whitespace-nowrap">
											{destination.startDate} - {destination.endDate}
										</p>
									</div>
								</div>
							</div>
						</div>
					</div>

					{/* Nights Counter */}
					<div className="h-[37.236px] relative shrink-0 w-[160px]">
						<div className="bg-clip-padding border-0 border-[transparent] border-solid content-stretch flex gap-[5.996px] items-center relative size-full">
							{/* Minus Button */}
							<button
								onClick={() => updateNights(destination.id, -1)}
								disabled={destination.nights <= 1}
								className="relative shrink-0 size-[11.992px] disabled:opacity-30 disabled:cursor-not-allowed hover:opacity-60 transition-opacity"
							>
								<svg className="absolute block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 11.9922 11.9922">
									<g>
										<path d="M2.49837 5.99609H9.49382" stroke="#0F3E4A" strokeLinecap="round" strokeLinejoin="round" strokeWidth="0.999349" />
									</g>
								</svg>
							</button>

							{/* Number Display */}
							<div className="bg-white h-[37.236px] relative rounded-[20971500px] shrink-0 w-[40px]">
								<div aria-hidden="true" className="absolute border-[#e5e7eb] border-[0.625px] border-solid inset-0 pointer-events-none rounded-[20971500px] shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_0px_rgba(0,0,0,0.1)]" />
								<div className="bg-clip-padding border-0 border-[transparent] border-solid relative size-full">
									<p className="-translate-x-1/2 absolute font-['Inter:Semi_Bold',sans-serif] font-semibold leading-[20px] left-[20px] not-italic text-[#0f3e4a] text-[14px] text-center top-[8px] tracking-[-0.1504px] whitespace-nowrap">
										{destination.nights}
									</p>
								</div>
							</div>

							{/* Plus Button */}
							<button
								onClick={() => updateNights(destination.id, 1)}
								className="relative shrink-0 size-[11.992px] hover:opacity-60 transition-opacity"
							>
								<svg className="absolute block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 11.9922 11.9922">
									<g>
										<path d="M2.49837 5.99609H9.49382" stroke="#0F3E4A" strokeLinecap="round" strokeLinejoin="round" strokeWidth="0.999349" />
										<path d="M5.99609 2.49837V9.49382" stroke="#0F3E4A" strokeLinecap="round" strokeLinejoin="round" strokeWidth="0.999349" />
									</g>
								</svg>
							</button>
						</div>
					</div>

					{/* Sleeping Button */}
					<div className="h-[17.998px] relative shrink-0 w-[160px]">
						<div className="bg-clip-padding border-0 border-[transparent] border-solid content-stretch flex items-start justify-center pl-[70.996px] pr-[71.006px] relative size-full">
							<button
								onClick={() => toggleAccommodation(destination.id)}
								className={`relative rounded-[20971500px] shrink-0 size-[17.998px] transition-all ${
									destination.hasAccommodation
										? "bg-[#ad46ff] shadow-[0px_1px_3px_0px_rgba(173,70,255,0.3),0px_1px_2px_0px_rgba(173,70,255,0.3)]"
										: "bg-gray-200 hover:bg-gray-300"
								}`}
							>
								<div className="bg-clip-padding border-0 border-[transparent] border-solid content-stretch flex items-center justify-center pl-[2.998px] pr-[3.008px] relative size-full">
									<svg className="relative shrink-0 size-[11.992px]" fill="none" preserveAspectRatio="none" viewBox="0 0 11.9922 11.9922">
										<g>
											<path
												d="M2.49837 5.99609H9.49382"
												stroke={destination.hasAccommodation ? "white" : "#9ca3af"}
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth="0.999349"
											/>
											<path
												d="M5.99609 2.49837V9.49382"
												stroke={destination.hasAccommodation ? "white" : "#9ca3af"}
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth="0.999349"
											/>
										</g>
									</svg>
								</div>
							</button>
						</div>
					</div>

					{/* Drag Handle Icon */}
					<div className="h-[15.996px] relative shrink-0 w-[23.994px]">
						<div className="bg-clip-padding border-0 border-[transparent] border-solid content-stretch flex items-center justify-center pl-[3.994px] pr-[4.004px] relative size-full">
							<div className="relative shrink-0 size-[15.996px] opacity-0 group-hover:opacity-100 transition-opacity">
								<svg className="absolute block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 15.9961 15.9961">
									<g>
										<path d={svgPaths.p3afb8200} stroke="#C27AFF" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.33301" />
										<path d={svgPaths.p29832600} stroke="#C27AFF" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.33301" />
										<path d={svgPaths.p1c5a8400} stroke="#C27AFF" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.33301" />
										<path d={svgPaths.p2795be00} stroke="#C27AFF" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.33301" />
										<path d={svgPaths.p22747e00} stroke="#C27AFF" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.33301" />
										<path d={svgPaths.pf16cf00} stroke="#C27AFF" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.33301" />
									</g>
								</svg>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

export function InteractiveRoutePlanner() {
	const [destinations, setDestinations] = useState<Destination[]>([
		{ id: "1", number: 1, name: "Colombo", startDate: "Mon 2 Dec", endDate: "Tue 3 Dec", nights: 2, hasAccommodation: true },
		{ id: "2", number: 2, name: "Sigiriya", startDate: "Wed 4 Dec", endDate: "Thu 5 Dec", nights: 3, hasAccommodation: true },
		{ id: "3", number: 3, name: "Ella", startDate: "Fri 6 Dec", endDate: "Sat 7 Dec", nights: 3, hasAccommodation: true },
		{ id: "4", number: 4, name: "Galle", startDate: "Sun 8 Dec", endDate: "Mon 9 Dec", nights: 4, hasAccommodation: true },
	]);

	const updateNights = useCallback((id: string, delta: number) => {
		setDestinations((prev) => prev.map((dest) => (dest.id === id ? { ...dest, nights: Math.max(1, dest.nights + delta) } : dest)));
	}, []);

	const toggleAccommodation = useCallback((id: string) => {
		setDestinations((prev) => prev.map((dest) => (dest.id === id ? { ...dest, hasAccommodation: !dest.hasAccommodation } : dest)));
	}, []);

	const addNewDestination = () => {
		const newId = (destinations.length + 1).toString();
		const newDestination: Destination = {
			id: newId,
			number: destinations.length + 1,
			name: "New Destination",
			startDate: "TBD",
			endDate: "TBD",
			nights: 2,
			hasAccommodation: false,
		};
		setDestinations([...destinations, newDestination]);
	};

	return (
		<div className="h-auto relative w-full bg-white rounded-lg shadow-sm overflow-hidden">
			{/* Header Row */}
			<div className="bg-[#f9fafb] h-[48.613px] relative shrink-0 w-full">
				<div aria-hidden="true" className="absolute border-[#dde9ed] border-b-[0.625px] border-solid inset-0 pointer-events-none" />
				<div className="flex flex-row items-center size-full">
					<div className="content-stretch flex gap-[31.992px] items-center pb-[0.625px] pl-[23.994px] pr-[79.98px] relative size-full">
						{/* Destination Header */}
						<div className="flex-[408.037_0_0] h-[15.996px] min-h-px min-w-px relative">
							<div className="bg-clip-padding border-0 border-[transparent] border-solid content-stretch flex gap-[11.992px] items-center relative size-full">
								<div className="relative shrink-0 size-[15.996px]">
									<svg className="absolute block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 15.9961 15.9961">
										<g clipPath="url(#clip0_11_833)">
											<path d={svgPaths.p7b7e400} stroke="#4F39F6" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.33301" />
											<path d={svgPaths.p22272e80} stroke="#4F39F6" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.33301" />
										</g>
										<defs>
											<clipPath id="clip0_11_833">
												<rect fill="white" height="15.9961" width="15.9961" />
											</clipPath>
										</defs>
									</svg>
								</div>
								<div className="h-[15.996px] relative shrink-0 w-[90.381px]">
									<div className="bg-clip-padding border-0 border-[transparent] border-solid relative size-full">
										<p className="absolute font-['Inter:Bold',sans-serif] font-bold leading-[16px] left-0 not-italic text-[#0f3e4a] text-[12px] top-[0.63px] tracking-[0.6px] uppercase whitespace-nowrap">
											Destination
										</p>
									</div>
								</div>
							</div>
						</div>

						{/* Nights Header */}
						<div className="h-[15.996px] relative shrink-0 w-[160px]">
							<div className="bg-clip-padding border-0 border-[transparent] border-solid content-stretch flex gap-[11.992px] items-center relative size-full">
								<div className="relative shrink-0 size-[11.992px]">
									<svg className="absolute block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 11.9922 11.9922">
										<g>
											<path d={svgPaths.pe56ed00} stroke="#9810FA" strokeLinecap="round" strokeLinejoin="round" strokeWidth="0.999349" />
										</g>
									</svg>
								</div>
								<div className="h-[15.996px] relative shrink-0 w-[51.191px]">
									<div className="bg-clip-padding border-0 border-[transparent] border-solid relative size-full">
										<p className="absolute font-['Inter:Bold',sans-serif] font-bold leading-[16px] left-0 not-italic text-[#0f3e4a] text-[12px] top-[0.63px] tracking-[0.6px] uppercase whitespace-nowrap">
											Nights
										</p>
									</div>
								</div>
							</div>
						</div>

						{/* Sleeping Header */}
						<div className="h-[15.996px] relative shrink-0 w-[160px]">
							<div className="bg-clip-padding border-0 border-[transparent] border-solid content-stretch flex gap-[11.992px] items-center relative size-full">
								<div className="relative shrink-0 size-[11.992px]">
									<svg className="absolute block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 11.9922 11.9922">
										<g>
											<path d={svgPaths.p2f631540} stroke="#AD46FF" strokeLinecap="round" strokeLinejoin="round" strokeWidth="0.999349" />
											<path d={svgPaths.p4b2ea00} stroke="#AD46FF" strokeLinecap="round" strokeLinejoin="round" strokeWidth="0.999349" />
										</g>
									</svg>
								</div>
								<div className="h-[15.996px] relative shrink-0 w-[65.225px]">
									<div className="bg-clip-padding border-0 border-[transparent] border-solid relative size-full">
										<p className="absolute font-['Inter:Bold',sans-serif] font-bold leading-[16px] left-0 not-italic text-[#0f3e4a] text-[12px] top-[0.63px] tracking-[0.6px] uppercase whitespace-nowrap">
											Sleeping
										</p>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Destination Rows */}
			{destinations.map((destination) => (
				<DestinationRow key={destination.id} destination={destination} updateNights={updateNights} toggleAccommodation={toggleAccommodation} />
			))}

			{/* Add New Destination */}
			<button
				onClick={addNewDestination}
				className="h-[58px] relative shrink-0 w-full border-t border-[#dde9ed] hover:bg-gray-50 transition-colors"
			>
				<div className="flex flex-row items-center justify-start size-full px-[23.994px]">
					<div className="content-stretch flex gap-[11.992px] items-center">
						<div className="relative shrink-0 size-[15.996px]">
							<svg className="absolute block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 15.9961 15.9961">
								<g>
									<circle cx="8" cy="8" r="7" stroke="#C27AFF" strokeWidth="1.2" fill="none" />
									<path d="M5 8H11" stroke="#C27AFF" strokeLinecap="round" strokeWidth="1.2" />
									<path d="M8 5V11" stroke="#C27AFF" strokeLinecap="round" strokeWidth="1.2" />
								</g>
							</svg>
						</div>
						<div className="h-[15.996px] relative">
							<p className="font-['Inter:Regular',sans-serif] font-normal leading-[16px] not-italic text-[#658c96] text-[13px] whitespace-nowrap">
								Add new destination...
							</p>
						</div>
					</div>
				</div>
			</button>
		</div>
	);
}
